import React from 'react';
import { Box, Tooltip } from '@mui/material';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined';
import { ThemeMode, useThemeMode } from '../theme/ThemeModeProvider';
import { dsVar, FONT_BODY } from '../theme/tokens';

const OPTIONS: { mode: ThemeMode; label: string; Icon: typeof DarkModeOutlinedIcon }[] = [
    { mode: 'light', label: 'Light', Icon: LightModeOutlinedIcon },
    { mode: 'dark', label: 'Dark', Icon: DarkModeOutlinedIcon },
    { mode: 'system', label: 'System', Icon: SettingsBrightnessOutlinedIcon },
];

const buttonReset = {
    border: 'none',
    cursor: 'pointer',
    fontFamily: FONT_BODY,
    transition: 'background-color 150ms, color 150ms',
} as const;

interface ThemeModeToggleProps {
    /** A single button that cycles through the modes, for the collapsed sidebar rail. */
    compact?: boolean;
}

/**
 * Light / Dark / System switch.
 *
 * Styled with `sx` and token variables rather than Tailwind: it also sits in the
 * account menu, which MUI portals outside #root where Tailwind classes don't apply.
 */
const ThemeModeToggle: React.FC<ThemeModeToggleProps> = ({ compact }) => {
    const { mode, setMode } = useThemeMode();

    if (compact) {
        const index = OPTIONS.findIndex(option => option.mode === mode);
        const current = OPTIONS[index];
        const next = OPTIONS[(index + 1) % OPTIONS.length];
        return (
            <Tooltip title={`Theme: ${current.label}`} placement="right">
                <Box
                    component="button"
                    type="button"
                    onClick={() => setMode(next.mode)}
                    aria-label={`Theme: ${current.label}. Switch to ${next.label}`}
                    sx={{
                        ...buttonReset,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        p: 1, borderRadius: '8px', backgroundColor: 'transparent', color: dsVar('fg-2'),
                        '&:hover': { backgroundColor: dsVar('surface'), color: dsVar('fg') },
                    }}
                >
                    <current.Icon sx={{ fontSize: 20 }} />
                </Box>
            </Tooltip>
        );
    }

    return (
        <Box
            role="radiogroup"
            aria-label="Theme"
            sx={{
                display: 'flex', width: '100%', gap: '2px', p: '2px',
                borderRadius: '8px', border: `1px solid ${dsVar('line')}`, backgroundColor: dsVar('page'),
            }}
        >
            {OPTIONS.map(({ mode: option, label, Icon }) => {
                const selected = option === mode;
                return (
                    <Box
                        key={option}
                        component="button"
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setMode(option)}
                        sx={{
                            ...buttonReset,
                            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                            px: 1, py: 0.5, borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600,
                            backgroundColor: selected ? dsVar('accent') : 'transparent',
                            color: selected ? dsVar('on-accent') : dsVar('fg-2'),
                            '&:hover': selected ? undefined : { color: dsVar('fg') },
                        }}
                    >
                        <Icon sx={{ fontSize: 14 }} />
                        {label}
                    </Box>
                );
            })}
        </Box>
    );
};

export default ThemeModeToggle;
